import { useState } from 'react';
import { can, type Me } from '../auth/me';
import { HistoryPanel } from '../history/HistoryPanel';
import { RowMenu } from '../ui/RowMenu';

export type ListRow = { id: string; name: string; notes?: string | undefined; details?: string | undefined; counts: number[] };

/**
 * The list of blocks or templates as a table (wygląd A): name with notes, counts on the right, the main action
 * ("Edytuj" / "Skład") as a button and the rarer ones (history, delete) in the row's "…" menu.
 */
export function ListTable<R extends ListRow>({
  me,
  label,
  resource,
  rows,
  countLabels,
  openLabel,
  openText,
  onOpen,
  onDelete,
}: {
  me: Me;
  label: string;
  resource: string;
  rows: R[];
  countLabels: string[];
  /** Accessible name of the open button of a row. */
  openLabel: (row: R) => string;
  openText: string;
  onOpen: (row: R) => void;
  onDelete: (row: R) => void;
}) {
  const [historyId, setHistoryId] = useState<string | null>(null);
  const seeHistory = can(me, 'history.entries', 'view');
  if (rows.length === 0) return null;
  const columns = 2 + countLabels.length;
  return (
    <div className="table-wrap">
      <table className="table tpl-table" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Nazwa</th>
            {countLabels.map((c) => (
              <th key={c} scope="col" className="num">
                {c}
              </th>
            ))}
            <th scope="col" className="tpl-actions-col">
              <span className="visually-hidden">Działania</span>
            </th>
          </tr>
        </thead>
        {rows.map((r) => {
          const more = [
            ...(seeHistory ? [{ label: historyId === r.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === r.id ? null : r.id) }] : []),
            ...(can(me, resource, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => onDelete(r) }] : []),
          ];
          return (
            <tbody key={r.id} aria-label={r.name}>
              <tr>
                <td>
                  <strong>{r.name}</strong>
                  {r.details && <div className="muted">{r.details}</div>}
                  {r.notes && <div className="muted">{r.notes}</div>}
                </td>
                {r.counts.map((n, i) => (
                  <td key={countLabels[i]} className="num">
                    {n}
                  </td>
                ))}
                <td className="tpl-actions">
                  <button className="btn small" type="button" aria-label={openLabel(r)} onClick={() => onOpen(r)}>
                    {openText}
                  </button>
                  <RowMenu label={`Więcej działań: ${r.name}`} actions={more} />
                </td>
              </tr>
              {historyId === r.id && seeHistory && (
                <tr>
                  <td colSpan={columns}>
                    <HistoryPanel entityId={r.id} resource={resource} />
                  </td>
                </tr>
              )}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
